import { Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import PageMeta from "../components/PageMeta";
import { trackSiteEvent } from "../lib/analytics";
import { Button } from "../components/ui/button";
import { CheckCircle, Star, Storefront, ArrowRight } from "@phosphor-icons/react";

const BASE = "https://thetrucktrackr.com";

const FREE_FEATURES = [
  "Live pin on the customer map",
  "Post one-off events & recurring weekly schedules",
  "Public profile page — logo, cover photo & description",
  "Customers can follow your truck & get push alerts",
  "Event inquiry leads from local event hosts",
  "Private events (catering, closed days) stay off the map",
];

const PRO_FEATURES = [
  "Your logo as your map pin",
  "Photo gallery on your profile",
  "Menu upload (PDF or image) + menu link",
  "Social links, hours & contact info",
  "Online ordering button",
  "Insights & analytics on profile views",
  "Google Calendar two-way sync",
  "Outlook Calendar two-way sync",
  "Apple Calendar feed for your staff",
  "Post Studio — weekly schedule graphics from your own photos",
  "Embed your live schedule on your own website",
  "Remove TruckTrackr branding from posts",
];

const FAQS = [
  {
    q: "Is TruckTrackr free for customers?",
    a: "Yes. Finding trucks on the map, following your favorites, and getting notifications is completely free — on the web and in the iPhone and Android apps.",
  },
  {
    q: "How much does TruckTrackr cost for food truck owners?",
    a: "The core listing is free forever: your truck on the live map, a public profile page, and schedule posting. TruckTrackr Pro is $15/month with a 14-day free trial.",
  },
  {
    q: "Does TruckTrackr take a commission or ordering fees?",
    a: "No. TruckTrackr is not an ordering platform — there are no commissions, no per-order fees, and no cut of your sales. Pro is a flat $15/month.",
  },
  {
    q: "Can I cancel Pro anytime?",
    a: "Yes. Cancel anytime from your billing settings — you keep Pro until the end of the billing period you've already paid for, and your truck keeps its free listing on the map. If a charge ever looks wrong, email support@thetrucktrackr.com and we'll sort it out.",
  },
];

const JSON_LD = [
  {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "TruckTrackr",
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web, iOS",
    url: `${BASE}/pricing`,
    offers: [
      { "@type": "Offer", name: "Free", price: "0", priceCurrency: "USD" },
      { "@type": "Offer", name: "Pro Monthly", price: "15.00", priceCurrency: "USD" },
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  },
];

export default function Pricing() {
  const { user } = useAuth();
  const signupTo = user?.role === "owner" ? "/dashboard" : "/signup?role=owner";
  return (
    <div className="mx-auto max-w-5xl px-4 sm:px-6 py-12 sm:py-16" data-testid="pricing-page">
      <PageMeta
        title="Food Truck Pricing — Free Listing & Pro Plan | TruckTrackr"
        description="Put your food truck on the live map for free. Upgrade to TruckTrackr Pro for $15/month — custom map pin, menu, calendar sync, schedule graphics and more. 14-day free trial."
        canonical={`${BASE}/pricing`}
        jsonLd={JSON_LD}
      />

      <div className="max-w-2xl">
        <span className="text-xs uppercase tracking-[0.2em] text-primary font-medium">For truck owners</span>
        <h1 className="mt-3 font-display font-bold tracking-tighter text-4xl sm:text-5xl">
          Simple pricing. <span className="text-primary">No commissions.</span>
        </h1>
        <p className="mt-4 text-base md:text-lg text-muted-foreground">
          Your truck on the map is free, forever. Pro adds the tools that turn
          map views into lines at your window. No ordering fees, no cut of your sales.
        </p>
      </div>

      <div className="mt-10 grid md:grid-cols-2 gap-5">
        {/* Free */}
        <div className="rounded-3xl border border-border bg-card p-6 sm:p-8 flex flex-col" data-testid="pricing-free-card">
          <div className="flex items-center gap-2">
            <Storefront weight="duotone" className="h-5 w-5 text-primary" />
            <h2 className="font-display font-bold text-lg">Free</h2>
          </div>
          <div className="mt-3 flex items-baseline gap-1">
            <span className="font-display font-bold text-4xl">$0</span>
            <span className="text-sm text-muted-foreground">/ forever</span>
          </div>
          <p className="mt-2 text-sm text-muted-foreground">
            Everything you need to get found by hungry locals.
          </p>
          <ul className="mt-5 space-y-2.5 flex-1">
            {FREE_FEATURES.map((f) => (
              <li key={f} className="flex items-start gap-2 text-sm">
                <CheckCircle weight="fill" className="h-4 w-4 text-primary flex-none mt-0.5" />
                {f}
              </li>
            ))}
          </ul>
          <Link to={signupTo} data-testid="pricing-free-cta" className="mt-6" onClick={() => trackSiteEvent("pricing_free_cta")}>
            <Button variant="outline" size="lg" className="w-full h-12 rounded-full gap-2">
              {user?.role === "owner" ? "Go to dashboard" : "Get on the map — free"}
              <ArrowRight className="h-4 w-4" />
            </Button>
          </Link>
        </div>

        {/* Pro */}
        <div className="rounded-3xl border-2 border-primary bg-primary/5 p-6 sm:p-8 flex flex-col relative" data-testid="pricing-pro-card">
          <div className="absolute -top-3 left-6 inline-flex items-center gap-1 rounded-full bg-primary text-primary-foreground px-3 py-1 text-[11px] font-bold uppercase tracking-wider">
            <Star weight="fill" className="h-3 w-3" /> 14-day free trial
          </div>
          <div className="flex items-center gap-2">
            <Star weight="fill" className="h-5 w-5 text-primary" />
            <h2 className="font-display font-bold text-lg">Pro</h2>
          </div>
          <div className="mt-3 flex items-baseline gap-1">
            <span className="font-display font-bold text-4xl">$15</span>
            <span className="text-sm text-muted-foreground">/ month</span>
          </div>
          <p className="mt-2 text-sm text-muted-foreground">
            Everything in Free, plus the full toolkit. Cancel anytime.
          </p>
          <ul className="mt-5 space-y-2.5 flex-1">
            {PRO_FEATURES.map((f) => (
              <li key={f} className="flex items-start gap-2 text-sm">
                <CheckCircle weight="fill" className="h-4 w-4 text-primary flex-none mt-0.5" />
                {f}
              </li>
            ))}
          </ul>
          <Link to={user ? "/upgrade" : "/signup?role=owner"} data-testid="pricing-pro-cta" className="mt-6" onClick={() => trackSiteEvent("pricing_pro_cta")}>
            <Button size="lg" className="w-full h-12 rounded-full gap-2 bg-primary text-primary-foreground hover:bg-primary/90">
              Start free trial
              <ArrowRight className="h-4 w-4" />
            </Button>
          </Link>
        </div>
      </div>

      {/* FAQ */}
      <section className="mt-16 max-w-3xl" data-testid="pricing-faq">
        <h2 className="font-display font-bold text-lg md:text-lg">Pricing questions</h2>
        <div className="mt-5 space-y-6">
          {FAQS.map((f) => (
            <div key={f.q}>
              <h3 className="font-semibold text-sm">{f.q}</h3>
              <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <div className="mt-14 rounded-3xl border border-border bg-card p-6 sm:p-8 text-center">
        <p className="text-sm text-muted-foreground">
          Just looking for lunch?{" "}
          <Link to="/map" className="text-primary font-semibold hover:underline" data-testid="pricing-map-link">
            Open the live map
          </Link>{" "}
          — it's free for everyone.
        </p>
      </div>
    </div>
  );
}
